import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Checkbox,
  Dropdown,
  Option,
  ProgressBar,
  makeStyles,
  shorthands,
  tokens,
  Field,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  Toaster,
  Tooltip,
  useToastController,
  useId,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  DocumentText20Regular,
  DocumentBulletList20Regular,
  DocumentSignature20Regular,
  Receipt20Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentDetails } from '../services/documentService';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';
import { fetchEmailTemplates, EmailTemplate } from '../services/emailTemplateService';
import { queryKeys } from '../lib/queryClient';
import RichTextEditor from './RichTextEditor';

interface CustomerSuggestion {
  id: string;
  name: string;
  email: string;
}

interface EmailHistoryItem {
  id: string;
  to: string;
  subject: string;
  templateKey?: string;
  status: string;
  sentAt?: string;
}

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '1000px',
    width: '100%',
    height: '80vh',
    maxHeight: '800px',
    '@media (max-width: 768px)': {
      maxWidth: '100vw',
      width: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      ...shorthands.borderRadius('0'),
    },
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  titleContainer: {
    flex: 1,
  },
  closeButton: {
    minWidth: 'auto',
  },
  dialogContent: {
    display: 'flex',
    gap: '16px',
    flex: 1,
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      ...shorthands.overflow('auto'),
    },
  },
  // Draft-invoice notice. Sits above the composer so the finalize requirement is
  // visible the moment the dialog opens, not only after a failed Send.
  draftBar: {
    ...shorthands.margin('0', '0', '12px', '0'),
    flexShrink: 0,
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  rightPanel: {
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('0', '0', '0', '16px'),
    '@media (max-width: 768px)': {
      width: '100%',
      ...shorthands.borderLeft('0'),
      ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
      ...shorthands.padding('16px', '0', '0', '0'),
    },
  },
  recipientRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  recipientLabel: {
    minWidth: '32px',
    ...shorthands.padding('12px', '0', '0', '0'),
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  recipientInputWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  ccToggles: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    flexShrink: 0,
    ...shorthands.padding('6px', '0', '0', '0'),
  },
  ccToggleButton: {
    minWidth: 'auto',
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  recipientInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    flexWrap: 'wrap',
  },
  suggestionsDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    marginTop: '4px',
    maxHeight: '240px',
    overflowY: 'auto',
    zIndex: 10,
  },
  suggestionItem: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('8px', '12px'),
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  suggestionItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  suggestionName: {
    fontSize: '14px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
  },
  suggestionEmail: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  suggestionEmpty: {
    ...shorthands.padding('12px'),
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  recipientTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ...shorthands.padding('4px', '8px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    fontSize: '14px',
  },
  removeButton: {
    minWidth: 'auto',
    width: '16px',
    height: '16px',
    ...shorthands.padding('0'),
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('8px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  attachmentIcon: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  attachmentInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  attachmentTitle: {
    fontSize: '14px',
    fontWeight: 600,
  },
  attachmentDate: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  historySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  historyEmpty: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    ...shorthands.padding('16px'),
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.padding('16px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    marginTop: '16px',
  },
});

interface SendDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentDetails | null;
  // Re-open the composer when the user hits "Undo" during the send grace
  // window (the dialog closes on Send; undo restores the unsent draft).
  onUndo?: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Gmail-style "undo send" grace period: clicking Send closes the composer and
// shows an undo toast; the email isn't actually sent until this many seconds
// pass without the user hitting Undo.
const UNDO_SECONDS = 5;

interface RecipientFieldProps {
  label: string;
  recipients: string[];
  onChange: (next: string[]) => void;
  customerPool: CustomerSuggestion[];
  placeholder?: string;
  autoFocus?: boolean;
  trailing?: React.ReactNode;
}

// One recipient row (To / Cc / Bcc): chip list + customer typeahead + free-form
// entry. Self-contained so all three rows share identical behavior.
const RecipientField: React.FC<RecipientFieldProps> = ({
  label,
  recipients,
  onChange,
  customerPool,
  placeholder,
  autoFocus,
  trailing,
}) => {
  const styles = useStyles();
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const matches = React.useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return customerPool
      .filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
      .filter((c) => !recipients.includes(c.email))
      .slice(0, 8);
  }, [input, customerPool, recipients]);

  useEffect(() => { setActiveIdx(0); }, [input]);

  const addFreeForm = () => {
    const addr = input.trim();
    if (addr && !recipients.includes(addr)) {
      onChange([...recipients, addr]);
      setInput('');
    }
  };

  const selectSuggestion = (s: CustomerSuggestion) => {
    if (!recipients.includes(s.email)) onChange([...recipients, s.email]);
    setInput('');
    setOpen(false);
  };

  const removeRecipient = (email: string) => {
    onChange(recipients.filter((r) => r !== email));
  };

  return (
    <div className={styles.recipientRow}>
      <div className={styles.recipientLabel}>{label}</div>
      <div className={styles.recipientInputWrapper}>
        <div className={styles.recipientInput}>
          {recipients.map((email) => (
            <div key={email} className={styles.recipientTag}>
              <span>{email}</span>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={() => removeRecipient(email)}
                className={styles.removeButton}
                aria-label={`Remove ${email}`}
              />
            </div>
          ))}
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder || 'Search by name or email...'}
            value={input}
            onChange={(_e, data) => {
              setInput(data.value);
              setOpen(!!data.value.trim());
            }}
            onFocus={() => { if (input.trim()) setOpen(true); }}
            onBlur={() => {
              // Accept whatever's typed when leaving the field, then close the menu.
              setTimeout(() => { addFreeForm(); setOpen(false); }, 150);
            }}
            onKeyDown={(e) => {
              if (open && matches.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActiveIdx((i) => Math.max(i - 1, 0));
                  return;
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  selectSuggestion(matches[activeIdx]);
                  return;
                }
                if (e.key === 'Escape') { setOpen(false); return; }
              }
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addFreeForm();
              }
            }}
            style={{ flex: 1, border: 'none', minWidth: '160px' }}
          />
        </div>
        {open && input.trim() && (
          <div className={styles.suggestionsDropdown}>
            {matches.length === 0 ? (
              <div className={styles.suggestionEmpty}>
                No customer match — press Enter to use as-is.
              </div>
            ) : (
              matches.map((m, idx) => (
                <div
                  key={m.id}
                  className={
                    idx === activeIdx
                      ? `${styles.suggestionItem} ${styles.suggestionItemActive}`
                      : styles.suggestionItem
                  }
                  onMouseDown={(e) => { e.preventDefault(); selectSuggestion(m); }}
                  onMouseEnter={() => setActiveIdx(idx)}
                >
                  <span className={styles.suggestionName}>{m.name || m.email}</span>
                  {m.name && <span className={styles.suggestionEmail}>{m.email}</span>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {trailing && <div className={styles.ccToggles}>{trailing}</div>}
    </div>
  );
};

// Wrap a disabled-for-cause button in a Tooltip explaining the reason. With no
// reason the button renders bare — avoids an empty-content Tooltip clobbering
// the button's own accessible label. Mirrors withFinalizeTooltip in
// DocumentEdit; same reserve/finalize-blocking pattern.
function withSendTooltip(reason: string | null, button: React.ReactElement) {
  if (!reason) return button;
  return (
    <Tooltip content={reason} relationship="label">
      {button}
    </Tooltip>
  );
}

const SendDocumentDialog: React.FC<SendDocumentDialogProps> = ({
  isOpen,
  onClose,
  document,
  onUndo,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('send-document-toaster');
  const undoToastId = useId('undo-send');
  const { dispatchToast, updateToast, dismissToast } = useToastController(toasterId);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [includePayLink, setIncludePayLink] = useState(false);
  // Waive the technology fee on the order's future Pay-Now charge. Seeded from
  // the order's persisted orders.waive_tech_fee (via the `document` prop); only
  // written back when the operator actually toggles it (`waiveTechFeeTouched`),
  // so an ordinary send never clobbers a waiver set from the payment dialog.
  const [waiveTechFee, setWaiveTechFee] = useState(false);
  const waiveTechFeeTouchedRef = React.useRef(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customerPool, setCustomerPool] = useState<CustomerSuggestion[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // In-dialog finalize (draft-invoice block). `locallyFinalized` records docs
  // finalized during this session so the block clears instantly, without
  // depending on the opener to refetch and re-pass `document`.
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [locallyFinalized, setLocallyFinalized] = useState<string[]>([]);
  const [history, setHistory] = useState<EmailHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);

  // Undo-send grace window. The actual /emails/send fires from a timer; the
  // snapshot payload lives in a ref so it survives the dialog closing (and so a
  // navigate-away during the window commits the send rather than dropping it).
  const undoTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingPayloadRef = React.useRef<any>(null);

  // Fetch the full customer list once when the dialog opens. Filtering is done
  // client-side so typing doesn't fire a network request per keystroke.
  useEffect(() => {
    if (!isOpen || customerPool.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/customers');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const pool: CustomerSuggestion[] = (data.data || [])
          .map((c: any) => ({ id: c.id, name: c.name || '', email: c.email || '' }))
          .filter((c: CustomerSuggestion) => c.email);
        setCustomerPool(pool);
      } catch {
        // Silent — recipient input still works as a free-form field.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, customerPool.length]);

  useEffect(() => {
    if (document) {
      // Pre-fill with customer email if available
      if (document.customerEmail) {
        setRecipients([document.customerEmail]);
      }
      // Fresh Cc/Bcc each time the dialog is populated for a document.
      setCcRecipients([]);
      setBccRecipients([]);
      setShowCc(false);
      setShowBcc(false);
      // Set default subject with invoice number
      setSubject(`Invoice #${document.number || ''}`);
      // Default-select the primary document: the document itself when opened
      // from a document, otherwise the order's invoice (the order id isn't an
      // attachable item, so fall back to the first invoice in the list).
      const docs = document.orderDocuments || [];
      const inList = docs.some((d) => d.id === document.id);
      const invoice = docs.find((d) => d.type === 'invoice');
      const defaultId = inList ? document.id : invoice?.id;
      setSelectedDocuments(defaultId ? [defaultId] : []);
      // Default the Pay Now link ON when this is an invoice send tied to an
      // order (the link needs the order id; resolved server-side from it).
      const hasInvoice = document.type === 'invoice' || !!invoice;
      setIncludePayLink(!!document.orderId && hasInvoice);
      // Seed the waive box from the order's persisted state; reset the touched
      // flag so we only persist a change the operator makes this session.
      setWaiveTechFee(!!document.orderWaivesTechFee);
      waiveTechFeeTouchedRef.current = false;
    }
  }, [document]);

  useEffect(() => {
    if (!isOpen) {
      setSendError(null);
      setIsSending(false);
      setFinalizing(false);
      setFinalizeError(null);
      // Drop the in-session finalize record; the next open re-reads `finalized`
      // from the freshly-passed document.
      setLocallyFinalized([]);
      // Clear the template selection so the dialog re-defaults to "Invoice send"
      // the next time it opens (see the auto-default effect below).
      setSelectedTemplate('');
    } else {
      // Reopening the composer must clear any abandoned waive toggle. The seeding
      // effect above keys on `document`, whose identity may not change between
      // opens for the same order — so reseed from the persisted state and reset
      // the touched flag here, or the next send could persist a change the
      // operator didn't make this session.
      setWaiveTechFee(!!document?.orderWaivesTechFee);
      waiveTechFeeTouchedRef.current = false;
    }
  }, [isOpen]);

  // Prefill the order's opted-in contacts (#1176) alongside the company email.
  // Contacts default opt-out, so only those explicitly opted in are added — the
  // billing contact is auto-opted-in, so it lands here too. Merges into (never
  // replaces) whatever the document effect set, and de-dupes case-insensitively.
  // The backend gate is the enforcement backstop; this is the visible convenience.
  useEffect(() => {
    const oid = document?.orderId;
    if (!isOpen || !oid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/orders/${encodeURIComponent(oid)}/opted-in-recipients`);
        if (!res.ok) return;
        const data = await res.json();
        const emails: string[] = (data.data || []).map((c: any) => c.email).filter(Boolean);
        if (cancelled || !emails.length) return;
        setRecipients((prev) => {
          const seen = new Set(prev.map((e) => e.toLowerCase()));
          const add = emails.filter((e) => !seen.has(e.toLowerCase()));
          return add.length ? [...prev, ...add] : prev;
        });
      } catch {
        // Non-fatal — staff can still add contacts manually.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, document?.orderId]);

  const loadHistory = React.useCallback(async () => {
    const orderId = document?.orderId;
    if (!orderId) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await apiFetch(
        `/emails/history?entity_type=order&entity_id=${encodeURIComponent(orderId)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      setHistory(data.data || []);
    } catch {
      // Non-fatal — the panel just shows the empty state.
    } finally {
      setHistoryLoading(false);
    }
  }, [document?.orderId]);

  // Load send history whenever the dialog opens for an order.
  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen, loadHistory]);

  const handleToggleDocument = (docId: string) => {
    setSelectedDocuments(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Open an attachable document (invoice/quote/receipt/landfill scan) in a new
  // browser tab — the same id forms /emails/send attaches, resolved server-side
  // to an inline PDF or the stored receipt image. The send dialog stays mounted
  // underneath, so the composed email is untouched. The tab is opened
  // synchronously inside the click so popup blockers don't kill it during the
  // authed fetch (apiFetch carries the admin token a raw navigation can't);
  // we navigate it once the blob is ready.
  const handleOpenDocument = async (docId: string) => {
    if (openingDocId) return;
    const tab = window.open('about:blank', '_blank');
    setOpeningDocId(docId);
    try {
      const res = await apiFetch(`/emails/documents/${encodeURIComponent(docId)}/preview`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Preview failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
      // Give the tab time to load before releasing the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      if (tab) tab.close();
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn’t open document</ToastTitle>
          <ToastBody>{e?.message || 'Preview failed'}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setOpeningDocId(null);
    }
  };

  // --- Templates (#20) ---------------------------------------------------
  // The template catalog (copy + variable rendering) is the backend's single
  // source of truth (backend/email_templates.py). When the dialog is tied to an
  // order, the subject/body come back already rendered against that order's real
  // variables; otherwise they carry the raw {token} strings. "Custom" is blank.
  const orderId = document?.orderId || null;
  const { data: templateData } = useQuery({
    queryKey: queryKeys.emailTemplates.list(orderId),
    queryFn: () => fetchEmailTemplates(orderId),
    enabled: isOpen,
  });
  const templates: EmailTemplate[] = templateData?.templates || [];

  const applyTemplate = (key: string) => {
    setSelectedTemplate(key);
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    // Custom returns blank subject/body; everything else prefills from the
    // server-rendered template. For non-custom we always overwrite, so
    // re-selecting a template re-applies its copy.
    const nextSubject = tpl.key === 'custom' ? subject : tpl.subject;
    const nextBody = tpl.body || '';
    setSubject(nextSubject);
    setEmailBody(nextBody);
    // Reflect the template's pay-link hint in the checkbox when there's an
    // order to resolve the link against.
    if (document?.orderId) setIncludePayLink(!!tpl.payLink);
    // Receipt template: pre-select the invoice + the most-recent payment receipt
    // (orderDocuments lists receipts most-recent-first), so "the receipt
    // alongside the invoice" is checked by default. Staff can add/remove others.
    if (key === 'receipt') {
      const docs = document?.orderDocuments || [];
      const invoiceId = docs.find((d) => d.type === 'invoice')?.id;
      const latestReceiptId = docs.find((d) => d.type === 'receipt')?.id;
      const preselect = [invoiceId, latestReceiptId].filter(Boolean) as string[];
      if (preselect.length) setSelectedDocuments(preselect);
    }
  };

  // Default the template selection to "Invoice send" once templates load.
  // Only fires when nothing is selected yet, so it never stomps a staff choice;
  // guarding on the key's presence lets it degrade gracefully if the template is
  // ever renamed/removed. The close effect resets selection so it re-defaults.
  useEffect(() => {
    if (!isOpen || selectedTemplate) return;
    if (templates.some((t) => t.key === 'invoice')) {
      applyTemplate('invoice');
    }
    // applyTemplate is stable enough for this purpose; re-running on template
    // identity change is intended (templates load asynchronously).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templates, selectedTemplate]);

  // --- Preview (#258) ----------------------------------------------------
  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await apiFetch('/emails/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: emailBody,
          body_format: 'html',
          include_pay_link: includePayLink,
          relatedEntityId: document?.orderId || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      setPreviewHtml(data.html || '<p>Preview unavailable.</p>');
    } catch {
      setPreviewHtml('<p>Preview unavailable.</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const clearUndoTimers = React.useCallback(() => {
    if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
    if (undoIntervalRef.current) { clearInterval(undoIntervalRef.current); undoIntervalRef.current = null; }
  }, []);

  // The actual network send. Receives the snapshot payload taken at Send time so
  // it's independent of any later edits to the (possibly reopened) composer.
  const performSend = React.useCallback(async (payload: any) => {
    dismissToast(undoToastId);
    setIsSending(true);
    try {
      const res = await apiFetch('/emails/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        const msg = data.error || `Failed to send email (${res.status})`;
        setSendError(msg);
        dispatchToast(
          <Toast>
            <ToastTitle>Email not sent</ToastTitle>
            <ToastBody>{msg}</ToastBody>
          </Toast>,
          { intent: 'error', position: 'top' }
        );
        return;
      }
      const count = data.sent || payload.recipients.length;
      const failedAttachments = (data.attachment_errors || []).length;
      // Contacts the backend skipped because they're opted out of email (#1176).
      const suppressed = (data.suppressed || []) as string[];
      dispatchToast(
        <Toast>
          <ToastTitle>Email sent</ToastTitle>
          <ToastBody>
            {`Sent to ${count} recipient${count > 1 ? 's' : ''}` +
              (data.attached ? ` with ${data.attached} attachment${data.attached > 1 ? 's' : ''}` : '') +
              (failedAttachments ? ` — ${failedAttachments} attachment${failedAttachments > 1 ? 's' : ''} could not be generated` : '') +
              (suppressed.length ? ` — skipped ${suppressed.length} opted-out contact${suppressed.length > 1 ? 's' : ''}` : '') +
              '.'}
          </ToastBody>
        </Toast>,
        { intent: failedAttachments || suppressed.length ? 'warning' : 'success', position: 'top' }
      );
      loadHistory();
    } catch (e: any) {
      const msg = e?.message || 'Network error';
      setSendError(msg);
      dispatchToast(
        <Toast>
          <ToastTitle>Email not sent</ToastTitle>
          <ToastBody>{msg}</ToastBody>
        </Toast>,
        { intent: 'error', position: 'top' }
      );
    } finally {
      setIsSending(false);
    }
  }, [dispatchToast, dismissToast, undoToastId, loadHistory]);

  // Cancel the pending send. We deliberately DON'T reopen the composer here —
  // an unrequested modal popping back feels jarring. Instead we keep the draft
  // and offer a "Reopen draft" action the user can take only if they want it.
  const handleUndo = React.useCallback(() => {
    clearUndoTimers();
    pendingPayloadRef.current = null;
    dismissToast(undoToastId);
    dispatchToast(
      <Toast appearance="inverted">
        <ToastTitle
          action={
            <ToastTrigger>
              <Button appearance="primary" size="small" onClick={() => onUndo?.()}>
                Reopen draft
              </Button>
            </ToastTrigger>
          }
        >
          Send canceled
        </ToastTitle>
        <ToastBody>Your draft was kept.</ToastBody>
      </Toast>,
      { intent: 'info', position: 'top', timeout: 8000 }
    );
  }, [clearUndoTimers, dismissToast, dispatchToast, undoToastId, onUndo]);

  // Prominent, hard-to-miss undo toast: high-contrast inverted surface, a primary
  // Undo button, and a draining progress bar so the grace window is obvious.
  const renderUndoToast = React.useCallback((secondsLeft: number) => (
    <Toast appearance="inverted">
      <ToastTitle>Sending email…</ToastTitle>
      <ToastBody>
        {/* Countdown, draining bar, and the Undo action are grouped together
            inside the toast body so the undo affordance reads as part of the
            toast rather than a detached title action. handleUndo dismisses the
            toast itself, so no ToastTrigger wrapper is needed. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '240px' }}>
          <span>{secondsLeft > 0 ? `Sends in ${secondsLeft}s — tap Undo to stop` : 'Sending…'}</span>
          <ProgressBar value={Math.max(0, secondsLeft) / UNDO_SECONDS} thickness="large" />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button appearance="primary" size="small" onClick={handleUndo}>Undo</Button>
          </div>
        </div>
      </ToastBody>
    </Toast>
  ), [handleUndo]);

  const startUndoWindow = React.useCallback((payload: any) => {
    clearUndoTimers();
    pendingPayloadRef.current = payload;
    // Close the composer immediately — the email now lives in the undo toast.
    onClose();
    let secondsLeft = UNDO_SECONDS;
    dispatchToast(renderUndoToast(secondsLeft), {
      toastId: undoToastId,
      // Generous lifetime so the toast can't auto-dismiss before the window
      // closes; we dismiss/replace it manually when the send fires.
      timeout: (UNDO_SECONDS + 2) * 1000,
      position: 'top',
      intent: 'info',
      politeness: 'assertive',
    });
    undoIntervalRef.current = setInterval(() => {
      secondsLeft = Math.max(0, secondsLeft - 1);
      updateToast({ toastId: undoToastId, content: renderUndoToast(secondsLeft), intent: 'info' });
    }, 1000);
    undoTimerRef.current = setTimeout(() => {
      clearUndoTimers();
      const p = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      if (p) performSend(p);
    }, UNDO_SECONDS * 1000);
  }, [clearUndoTimers, onClose, dispatchToast, updateToast, renderUndoToast, undoToastId, performSend]);

  // Draft-invoice send guard. A pro forma (unfinalized) invoice is not a legal
  // invoice and must not be emailed to a customer. Only known drafts block
  // (finalized === false); the backend /emails/send guard is the fail-closed
  // backstop for any data gap. Invoice-only: receipts/POs/quotes never block.
  //
  // The blocked state is surfaced as a persistent MessageBar with a "Finalize
  // now" action (see the render), NOT as a disabled button with a hover-only
  // tooltip. A `disabledFocusable` Send whose reason lived solely in a Tooltip
  // read as "the Send button does nothing" to operators who clicked without
  // hovering — and the remedy was on another screen (DocumentEdit), costing a
  // full re-compose of the email. Keep the requirement visible on open, and put
  // the fix next to the explanation.
  const selectedDraftInvoice = React.useMemo(() => {
    const docs = document?.orderDocuments || [];
    return (
      docs.find(
        (d) =>
          selectedDocuments.includes(d.id) &&
          d.type === 'invoice' &&
          d.finalized === false &&
          !locallyFinalized.includes(d.id),
      ) || null
    );
  }, [document, selectedDocuments, locallyFinalized]);

  const draftInvoiceBlockedReason = React.useMemo<string | null>(() => {
    if (!selectedDraftInvoice) return null;
    const label =
      selectedDraftInvoice.number && selectedDraftInvoice.number !== 'Draft invoice'
        ? ` #${selectedDraftInvoice.number}`
        : '';
    return `This invoice${label} is a draft. Finalize it before emailing it to a customer.`;
  }, [selectedDraftInvoice]);

  // Clear a stale finalize failure when the operator changes which docs are
  // attached — the previous error no longer describes the current selection.
  useEffect(() => {
    setFinalizeError(null);
  }, [selectedDraftInvoice?.id]);

  // Finalize the attached draft in place, so the operator never has to leave the
  // composer. Mirrors DocumentEdit's handleFinalize, but surfaces the backend's
  // guard reasons (order not reserved, PO required) inline instead of only
  // console.error-ing them — those are the two ways this legitimately fails.
  const handleFinalizeDraftInvoice = React.useCallback(async () => {
    if (!selectedDraftInvoice) return;
    setFinalizing(true);
    setFinalizeError(null);
    try {
      const res = await apiFetch(`/booqable/documents/${selectedDraftInvoice.id}/finalize`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setFinalizeError(body.error || `Could not finalize this invoice (${res.status}).`);
        return;
      }
      // Clear the block immediately rather than waiting on the opener to refetch
      // and re-pass `document` — the openers don't share one query key.
      setLocallyFinalized((ids) => [...ids, selectedDraftInvoice.id]);
      setSendError(null);
      queryClient.invalidateQueries({ queryKey: ['order-documents'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Invoice finalized</ToastTitle>
          <ToastBody>It now has an invoice number and can be emailed.</ToastBody>
        </Toast>,
        { intent: 'success', position: 'top' },
      );
    } catch (e: any) {
      setFinalizeError(e?.message || 'Network error while finalizing.');
    } finally {
      setFinalizing(false);
    }
  }, [selectedDraftInvoice, queryClient, dispatchToast]);

  const handleSend = () => {
    setSendError(null);
    if (draftInvoiceBlockedReason) {
      setSendError(draftInvoiceBlockedReason);
      return;
    }

    if (recipients.length === 0) {
      setSendError('Add at least one recipient');
      return;
    }
    const invalid = [...recipients, ...ccRecipients, ...bccRecipients].filter((r) => !EMAIL_RE.test(r));
    if (invalid.length > 0) {
      setSendError(`Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`);
      return;
    }
    if (!subject.trim()) {
      setSendError('Subject is required');
      return;
    }

    startUndoWindow({
      recipients,
      cc: ccRecipients,
      bcc: bccRecipients,
      subject: subject.trim(),
      body: emailBody,
      body_format: 'html',
      document_ids: selectedDocuments,
      relatedEntityType: document?.orderId ? 'order' : undefined,
      relatedEntityId: document?.orderId || undefined,
      include_pay_link: includePayLink,
      // Persist the tech-fee waiver to orders.waive_tech_fee ONLY when the
      // operator toggled it this session — otherwise omit the key so the send
      // leaves any existing waiver (set from the payment dialog) untouched.
      waive_tech_fee: waiveTechFeeTouchedRef.current ? waiveTechFee : undefined,
      // Which prefill template this send started from (#20). Logged to
      // email_sends.template_key; blank when the user wrote from scratch.
      template_key: selectedTemplate || undefined,
    });
  };

  // On unmount mid-window (e.g. the user navigates away), commit the pending
  // send rather than silently dropping it — better to send than to lose an
  // invoice email the user believes is on its way.
  useEffect(() => {
    return () => {
      const p = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      clearUndoTimers();
      if (p) performSend(p);
    };
  }, [clearUndoTimers, performSend]);

  const formatDocumentType = (type: string) => {
    const label = (type || '').replace(/_/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const documentIcon = (type: string) => {
    switch (type) {
      case 'quote':
        return <DocumentBulletList20Regular />;
      case 'contract':
        return <DocumentSignature20Regular />;
      case 'landfill_receipt':
      case 'receipt':
        return <Receipt20Regular />;
      default:
        return <DocumentText20Regular />;
    }
  };

  const formatCurrency = (amount: number) => {
    return amount > 0 ? `$${amount.toFixed(2)}` : '';
  };

  if (!document) return null;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <DialogTitle>New email</DialogTitle>
            </div>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              className={styles.closeButton}
              aria-label="Close"
            />
          </div>
          {/* Draft-invoice block, stated up front with its remedy attached. The
              operator sees this on open — before composing — and can finalize
              without leaving the dialog. */}
          {draftInvoiceBlockedReason && (
            <MessageBar
              intent={finalizeError ? 'error' : 'warning'}
              className={styles.draftBar}
            >
              <MessageBarBody>
                <MessageBarTitle>
                  {finalizeError ? 'Could not finalize' : 'This invoice is a draft'}
                </MessageBarTitle>{' '}
                {finalizeError ||
                  'A draft is a pro forma, not a legal invoice. Finalize it to assign its invoice number, then send.'}
              </MessageBarBody>
              <MessageBarActions>
                <Button
                  appearance="primary"
                  size="small"
                  disabled={finalizing || isSending}
                  onClick={handleFinalizeDraftInvoice}
                >
                  {finalizing ? 'Finalizing…' : finalizeError ? 'Try again' : 'Finalize now'}
                </Button>
              </MessageBarActions>
            </MessageBar>
          )}
          <DialogContent className={styles.dialogContent}>
            {/* Left Panel - Email Composer */}
            <div className={styles.leftPanel}>
              {/* Recipients — To / Cc / Bcc, real email-client style */}
              <RecipientField
                label="To"
                recipients={recipients}
                onChange={setRecipients}
                customerPool={customerPool}
                trailing={
                  (!showCc || !showBcc) ? (
                    <>
                      {!showCc && (
                        <Button
                          appearance="subtle"
                          size="small"
                          className={styles.ccToggleButton}
                          onClick={() => setShowCc(true)}
                        >
                          Cc
                        </Button>
                      )}
                      {!showBcc && (
                        <Button
                          appearance="subtle"
                          size="small"
                          className={styles.ccToggleButton}
                          onClick={() => setShowBcc(true)}
                        >
                          Bcc
                        </Button>
                      )}
                    </>
                  ) : undefined
                }
              />
              {showCc && (
                <RecipientField
                  label="Cc"
                  recipients={ccRecipients}
                  onChange={setCcRecipients}
                  customerPool={customerPool}
                  autoFocus
                />
              )}
              {showBcc && (
                <RecipientField
                  label="Bcc"
                  recipients={bccRecipients}
                  onChange={setBccRecipients}
                  customerPool={customerPool}
                  autoFocus
                />
              )}

              {/* Template + Subject — inline on one row */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Dropdown
                  placeholder="Select a template..."
                  value={templates.find((t) => t.key === selectedTemplate)?.label || ''}
                  selectedOptions={selectedTemplate ? [selectedTemplate] : []}
                  onOptionSelect={(e, data) => applyTemplate(data.optionValue as string)}
                  style={{ minWidth: '180px', flexShrink: 0 }}
                >
                  {templates.map((t) => (
                    <Option key={t.key} value={t.key}>
                      {t.label}
                    </Option>
                  ))}
                </Dropdown>
                <Input
                  placeholder="Subject"
                  value={subject}
                  onChange={(e, data) => setSubject(data.value)}
                  style={{ flex: 1 }}
                />
              </div>

              {/* Email Body Editor — shared rich text (innerHTML, sanitized server-side) */}
              <RichTextEditor value={emailBody} onChange={setEmailBody} />
            </div>

            {/* Right Panel - Attachments & History */}
            <div className={styles.rightPanel}>
              {/* Attach Documents Section */}
              <div>
                <div className={styles.sectionTitle}>Attach documents</div>
                {document.orderDocuments && document.orderDocuments.length > 0 ? (
                  document.orderDocuments.map((doc) => (
                    <div key={doc.id} className={styles.attachmentItem} style={{ marginBottom: '8px' }}>
                      <Checkbox
                        checked={selectedDocuments.includes(doc.id)}
                        onChange={() => handleToggleDocument(doc.id)}
                      />
                      <span className={styles.attachmentIcon}>{documentIcon(doc.type)}</span>
                      <div className={styles.attachmentInfo}>
                        <div className={styles.attachmentTitle}>
                          {formatDocumentType(doc.type)}
                          {/* Landfill + payment receipts carry a descriptive
                              label in `number` (weighed line / method · date);
                              show it as a subtitle, not a "#" document number. */}
                          {doc.type !== 'landfill_receipt' && doc.type !== 'receipt' && doc.type !== 'purchase_order' && doc.number && ` #${doc.number}`}
                          {doc.amount > 0 && ` (${formatCurrency(doc.amount)})`}
                        </div>
                        {(doc.type === 'landfill_receipt' || doc.type === 'receipt' || doc.type === 'purchase_order') && doc.number ? (
                          <div className={styles.attachmentDate}>{doc.number}</div>
                        ) : (
                          doc.date && <div className={styles.attachmentDate}>{doc.date}</div>
                        )}
                      </div>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Open16Regular />}
                        disabled={!!openingDocId}
                        onClick={() => handleOpenDocument(doc.id)}
                        aria-label={`Open ${formatDocumentType(doc.type)} in a new tab`}
                        title="Open in new tab"
                      />
                    </div>
                  ))
                ) : (
                  <div className={styles.historyEmpty}>
                    No documents found for this order
                  </div>
                )}
              </div>

              {/* Online payment link — needs an order to resolve the invoice */}
              {document.orderId && (
                <div style={{ marginTop: '16px' }}>
                  <div className={styles.sectionTitle}>Payment</div>
                  <Checkbox
                    checked={includePayLink}
                    onChange={(_, d) => setIncludePayLink(!!d.checked)}
                    label='Include a "Pay Now" link in the email'
                  />
                  <Checkbox
                    checked={waiveTechFee}
                    onChange={(_, d) => {
                      waiveTechFeeTouchedRef.current = true;
                      setWaiveTechFee(!!d.checked);
                    }}
                    label="Waive technology fee on the customer's payment"
                  />
                </div>
              )}

              {/* History Section */}
              <div className={styles.historySection}>
                <div className={styles.sectionTitle}>History</div>
                {historyLoading ? (
                  <div className={styles.historyEmpty}>Loading…</div>
                ) : history.length === 0 ? (
                  <div className={styles.historyEmpty}>
                    There are no sent emails for this order
                  </div>
                ) : (
                  history.map((h) => (
                    <div key={h.id} className={styles.attachmentItem} style={{ marginBottom: '8px' }}>
                      <div className={styles.attachmentInfo}>
                        <div className={styles.attachmentTitle}>{h.subject}</div>
                        <div className={styles.attachmentDate}>
                          {h.to}
                          {h.sentAt && ` · ${formatBusinessDateTime(h.sentAt)}`}
                          {h.status !== 'sent' && ` · ${h.status}`}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <Button appearance="secondary" disabled={isSending} onClick={handlePreview}>Preview</Button>
              {sendError && (
                <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '13px' }}>
                  {sendError}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button appearance="secondary" onClick={onClose} disabled={isSending}>
                Cancel
              </Button>
              {/* Deliberately NOT disabled/disabledFocusable, and no longer
                  wrapped in withSendTooltip. A dead primary button reads as a
                  broken app to anyone who clicks without hovering first, and
                  the Tooltip's relationship="label" replaced the button's
                  accessible name with the draft reason — so screen readers
                  announced the block instead of "Send". The MessageBar above
                  carries the explanation persistently; clicking here restates
                  it inline via handleSend → sendError. */}
              <Button
                appearance="primary"
                onClick={handleSend}
                disabled={isSending || finalizing}
              >
                {isSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* Preview overlay (#258) — renders the assembled, sanitized HTML exactly as
        the recipient will receive it, in an isolated iframe. */}
    <Dialog open={previewOpen} onOpenChange={(_, data) => !data.open && setPreviewOpen(false)}>
      <DialogSurface style={{ maxWidth: '640px', width: '100%' }}>
        <DialogBody>
          <DialogTitle>Email preview</DialogTitle>
          <DialogContent>
            {previewLoading ? (
              <div className={styles.historyEmpty}>Loading preview…</div>
            ) : (
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                style={{ width: '100%', height: '60vh', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: '4px', background: '#fff' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* Mounted outside Dialog so a success toast survives the dialog closing. */}
    <Toaster toasterId={toasterId} />
    </>
  );
};

export default SendDocumentDialog;
